import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, ReferenceLine,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Star, TrendingDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtSplit = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const t = Math.floor((seconds * 10) % 10);
  return `${m}:${s.toString().padStart(2, '0')}.${t}`;
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function TrendTooltip({ active, payload, benchmarkSplit }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const delta = benchmarkSplit ? p.avg_split - benchmarkSplit : null;
  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg p-2 shadow-lg text-xs">
      <div className="font-bold text-zinc-900 dark:text-white">
        {p.session_name || 'Session'}
      </div>
      <div className="text-zinc-500 dark:text-zinc-400">
        {new Date(p.start_time).toLocaleDateString()} · {fmtSplit(p.avg_split)}/500m
      </div>
      {delta !== null && Math.abs(delta) > 0.1 && (
        <div className={`font-bold ${delta < 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {delta < 0 ? '−' : '+'}{Math.abs(delta).toFixed(1)}s vs benchmark
        </div>
      )}
      {p.is_benchmark && (
        <div className="text-amber-600 font-bold flex items-center gap-1 mt-0.5">
          <Star className="w-3 h-3 fill-current" /> Benchmark
        </div>
      )}
    </div>
  );
}

/**
 * Distance-from-Benchmark sparkline.
 * Renders one dot per same-boat session: green when the avg_split is at-or-better
 * than the benchmark, rose when slower. The benchmark itself shows as a horizontal
 * reference line and a starred amber dot. The "current" workout is enlarged so the
 * user can spot it among the historical points.
 */
export default function BenchmarkTrend({ boatType, currentWorkoutId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!boatType) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(
          `${API}/workouts/benchmark-trend?boat_type=${encodeURIComponent(boatType)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (!cancelled) setData(d);
      } catch (e) {
        console.error('benchmark-trend load error:', e);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [boatType]);

  const series = useMemo(() => {
    if (!data?.sessions?.length) return [];
    return data.sessions.map((s, idx) => ({
      ...s,
      idx,
      ts: new Date(s.start_time).getTime(),
      isCurrent: s.workout_id === currentWorkoutId,
    }));
  }, [data, currentWorkoutId]);

  const benchmarkSplit = data?.benchmark?.avg_split || null;
  const renderTooltip = useCallback(
    (props) => <TrendTooltip {...props} benchmarkSplit={benchmarkSplit} />,
    [benchmarkSplit]
  );

  if (loading) return null;
  if (!data || series.length < 2) return null; // Need ≥2 sessions for a trend

  const allSplits = series.map((s) => s.avg_split);
  const minSplit = Math.min(...allSplits);
  const maxSplit = Math.max(...allSplits);
  const pad = Math.max(1, (maxSplit - minSplit) * 0.15);

  return (
    <div
      className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5"
      data-testid="benchmark-trend"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
          <TrendingDown className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-zinc-900 dark:text-white">
            Distance from Benchmark
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400">
            {benchmarkSplit
              ? `${data.boat_type} sessions — every dot is one row.`
              : `${data.boat_type} sessions — set a benchmark to see how close each row gets to your bar.`}
          </div>
        </div>
        {benchmarkSplit && (
          <div className="hidden sm:flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
            <Star className="w-3.5 h-3.5 fill-current" />
            {fmtSplit(benchmarkSplit)}
          </div>
        )}
      </div>

      <div className="h-40 sm:h-48" data-testid="benchmark-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis
              type="number"
              dataKey="ts"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(t) => fmtDate(new Date(t).toISOString())}
              stroke="#71717a"
              fontSize={11}
              tickCount={6}
            />
            <YAxis
              type="number"
              dataKey="avg_split"
              reversed
              domain={[minSplit - pad, maxSplit + pad]}
              tickFormatter={(t) => fmtSplit(t)}
              stroke="#71717a"
              fontSize={11}
              width={56}
            />
            <ZAxis dataKey="idx" range={[40, 40]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={renderTooltip}
            />
            {benchmarkSplit && (
              <ReferenceLine
                y={benchmarkSplit}
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={{
                  value: 'Benchmark',
                  position: 'insideTopRight',
                  fill: '#b45309',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />
            )}
            <Scatter data={series} isAnimationActive={false}>
              {series.map((entry) => {
                const isBetter = benchmarkSplit ? entry.avg_split <= benchmarkSplit : false;
                const fill = entry.is_benchmark
                  ? '#f59e0b'
                  : isBetter ? '#10b981' : '#f43f5e';
                const r = entry.isCurrent ? 8 : 5;
                return (
                  <Cell
                    key={entry.workout_id}
                    fill={fill}
                    stroke={entry.isCurrent ? '#0f172a' : 'transparent'}
                    strokeWidth={entry.isCurrent ? 2 : 0}
                    r={r}
                  />
                );
              })}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
          <span className="text-zinc-600 dark:text-zinc-400">Beats benchmark</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-rose-500"></span>
          <span className="text-zinc-600 dark:text-zinc-400">Slower than benchmark</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-amber-500"></span>
          <span className="text-zinc-600 dark:text-zinc-400">Benchmark</span>
        </span>
        <span className="hidden sm:flex items-center gap-1.5 ml-auto">
          <span className="w-4 h-4 rounded-full border-2 border-zinc-900 dark:border-white"></span>
          <span className="text-zinc-600 dark:text-zinc-400">This session</span>
        </span>
      </div>
    </div>
  );
}
